// Reactive store mirroring MainView.swift's notes/folders Firestore listeners.
// Owns: users/{uid}/notes (ordered by timestamp desc) + users/{uid}/folders (ordered by createdAt asc).

import { reactive, readonly, watch } from "vue";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { deleteObject, listAll, ref as storageRef } from "firebase/storage";
import { db, storage } from "../firebase";
import { deleteNoteAssets } from "../utils/noteStorageCleanup";
import { authState } from "./authStore";

// Mirrors MainView.swift's 25-note first page.
export const PAGE_SIZE = 25;

const state = reactive({
  notes: [],
  sharedNotes: [],
  folders: [],
  isLoading: true,
  isLoadingMore: false,
  hasMore: false,
  error: null,
});

// Dashboard UI state — kept in the store so navigating into a note and
// pressing "Back to notes" restores the active category, folder, and
// search the user had selected. Resets only on signout/listener detach.
export const dashboardUiState = reactive({
  activeCategory: "ALL",
  activeFolder: null,
  searchTerm: "",
});

let notesUnsub = null;
let foldersUnsub = null;
let attachedUid = null;
let currentLimit = PAGE_SIZE;
let stopSharedWatcher = null;
let lastSharedSignature = "";

function normalizeExercises(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => {
      if (!e || typeof e !== "object") return null;
      const instructions = typeof e.instructions === "string" ? e.instructions : null;
      const responses = typeof e.responses === "string" ? e.responses : null;
      if (!instructions || !responses) return null;
      return { instructions, responses };
    })
    .filter(Boolean);
}

function normalizeQuizzes(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((q) => {
      if (!q || typeof q !== "object") return null;
      const question = typeof q.question === "string" ? q.question : null;
      const answers = Array.isArray(q.answers) ? q.answers.filter((a) => typeof a === "string") : [];
      const response = q.response;
      if (!question || answers.length === 0 || response == null) return null;
      return { question, answers, response };
    })
    .filter(Boolean);
}

function normalizeNote(id, data, ownerID, ownerName) {
  const folder = typeof data.folder === "string" && data.folder.length > 0 ? data.folder : null;
  const noteType = typeof data.noteType === "string" && data.noteType.length > 0 ? data.noteType : null;
  // The iOS app persists exercises under the French key "exercices" (NoteView.swift:544)
  // and quizzes under "quizzes". We accept either spelling defensively.
  const rawExercises = data.exercices || data.exercises;
  const rawQuizzes = data.quizzes || data.quizz;
  return {
    id: id,
    content: typeof data.content === "string" ? data.content : "",
    timestamp: data.timestamp || null,
    noteType,
    deleted: data.deleted === true,
    folder,
    isLoading: data.isLoading === true,
    fileURLs: Array.isArray(data.fileURLs) ? data.fileURLs : [],
    mindmap: data.mindmap || null,
    // The paper the note is written on (iOS `NotePaperStyle`), as stored.
    paper: typeof data.paper === "string" ? data.paper : null,
    sharedWith: Array.isArray(data.sharedWith) ? data.sharedWith : [],
    quizzes: normalizeQuizzes(rawQuizzes),
    exercises: normalizeExercises(rawExercises),
    _ownerID: ownerID || null,
    _ownerName: ownerName || null,
    _shared: !!ownerID,
  };
}

function normalizeFolder(id, data) {
  let color = [0.4, 0.6, 0.95, 1];
  if (Array.isArray(data.color) && data.color.length >= 3) {
    color = [
      Number(data.color[0]) || 0,
      Number(data.color[1]) || 0,
      Number(data.color[2]) || 0,
      data.color[3] != null ? Number(data.color[3]) : 1,
    ];
  }
  return {
    id,
    name: typeof data.name === "string" ? data.name : id,
    color,
    // Subject the iOS app picked for this folder ("" = plain folder).
    subject: typeof data.subject === "string" ? data.subject : undefined,
    createdAt: data.createdAt || null,
  };
}

function subscribeNotes(uid, lim) {
  if (notesUnsub) {
    notesUnsub();
    notesUnsub = null;
  }
  state.isLoadingMore = state.notes.length > 0;
  if (!state.notes.length) state.isLoading = true;

  const notesRef = query(
    collection(db, "users", uid, "notes"),
    orderBy("timestamp", "desc"),
    fsLimit(lim),
  );
  notesUnsub = onSnapshot(
    notesRef,
    (snap) => {
      state.notes = snap.docs.map((d) => normalizeNote(d.id, d.data() || {}, null, null));
      state.hasMore = snap.size >= lim;
      state.isLoading = false;
      state.isLoadingMore = false;
    },
    (err) => {
      console.error("notes listener error:", err);
      state.error = err && err.message ? err.message : "notes listener failed";
      state.isLoading = false;
      state.isLoadingMore = false;
    },
  );
}

async function refreshSharedNotes() {
  const entries = (authState.userData && authState.userData.sharedNotes) || [];
  const signature = entries
    .map((e) => `${e.userID}|${e.noteID}|${e.name || ""}`)
    .sort()
    .join(",");
  if (signature === lastSharedSignature) return;
  lastSharedSignature = signature;
  if (entries.length === 0) {
    state.sharedNotes = [];
    return;
  }
  const results = await Promise.all(
    entries.map(async (e) => {
      try {
        const snap = await getDoc(doc(db, "users", e.userID, "notes", e.noteID));
        if (!snap.exists()) return null;
        return normalizeNote(snap.id, snap.data() || {}, e.userID, e.name || "");
      } catch (err) {
        console.warn("shared note fetch failed:", err);
        return null;
      }
    }),
  );
  state.sharedNotes = results.filter(Boolean);
}

export function attachNotesListeners(uid) {
  if (!uid || attachedUid === uid) return;
  detachNotesListeners();
  attachedUid = uid;
  state.isLoading = true;
  state.error = null;
  currentLimit = PAGE_SIZE;

  subscribeNotes(uid, currentLimit);

  // Watch authState.userData.sharedNotes and refetch the underlying owner notes.
  stopSharedWatcher = watch(
    () => (authState.userData && authState.userData.sharedNotes) || [],
    () => refreshSharedNotes(),
    { immediate: true, deep: true },
  );

  const foldersRef = query(
    collection(db, "users", uid, "folders"),
    orderBy("createdAt", "asc"),
  );
  foldersUnsub = onSnapshot(
    foldersRef,
    (snap) => {
      state.folders = snap.docs.map((d) => normalizeFolder(d.id, d.data() || {}));
    },
    (err) => {
      // Folders without a createdAt index would error; fall back to unordered.
      console.warn("folders listener (ordered) failed, retrying unordered:", err);
      if (foldersUnsub) {
        foldersUnsub();
        foldersUnsub = null;
      }
      const fallback = collection(db, "users", uid, "folders");
      foldersUnsub = onSnapshot(
        fallback,
        (snap) => {
          state.folders = snap.docs.map((d) => normalizeFolder(d.id, d.data() || {}));
        },
        (err2) => console.error("folders listener fallback failed:", err2),
      );
    },
  );
}

export function loadMoreNotes() {
  if (!attachedUid) return;
  if (state.isLoadingMore || state.isLoading) return;
  if (!state.hasMore) return;
  currentLimit += PAGE_SIZE;
  subscribeNotes(attachedUid, currentLimit);
}

export function detachNotesListeners() {
  if (notesUnsub) {
    notesUnsub();
    notesUnsub = null;
  }
  if (foldersUnsub) {
    foldersUnsub();
    foldersUnsub = null;
  }
  if (stopSharedWatcher) {
    stopSharedWatcher();
    stopSharedWatcher = null;
  }
  attachedUid = null;
  currentLimit = PAGE_SIZE;
  lastSharedSignature = "";
  state.notes = [];
  state.sharedNotes = [];
  state.folders = [];
  state.isLoading = true;
  state.isLoadingMore = false;
  state.hasMore = false;
  state.error = null;
  dashboardUiState.activeCategory = "ALL";
  dashboardUiState.activeFolder = null;
  dashboardUiState.searchTerm = "";
}

// Default folder categories (mirroring MainView.swift sidebar).
// `icon` is an SfIcon name, `tint` the tile colour — iOS system colours, as
// on the app's settings rows.
// `emoji` names the 3D emoji iOS draws for the category (see EMOJI in
// assets/index.js); `icon` is the flat glyph used where a picture is too big.
export const DEFAULT_CATEGORIES = [
  { key: "ALL", icon: "library", emoji: "spiralNotepad" },
  { key: "SHARED", icon: "friends", emoji: "student" },
  { key: "AUDIO", icon: "noteAudio", emoji: "studioMic" },
  { key: "IMPORT", icon: "noteImport", emoji: "cardIndex" },
  { key: "SCAN", icon: "noteScan", emoji: "cameraFlash" },
  { key: "LESSON", icon: "noteLesson", emoji: "books" },
  { key: "TRASH", icon: "trash", emoji: "wastebasket" },
];

export function filterNotes(notes, categoryKey, folderName, sharedNotes = []) {
  if (folderName) {
    return notes.filter((n) => !n.deleted && n.folder === folderName);
  }
  if (categoryKey === "SHARED") {
    return (sharedNotes || []).filter((n) => !n.deleted);
  }
  if (!categoryKey || categoryKey === "ALL") {
    return notes.filter((n) => !n.deleted);
  }
  if (categoryKey === "TRASH") {
    return notes.filter((n) => n.deleted);
  }
  return notes.filter((n) => !n.deleted && n.noteType === categoryKey);
}

export function countByCategory(notes, categoryKey, sharedNotes = []) {
  return filterNotes(notes, categoryKey, null, sharedNotes).length;
}

export function countByFolder(notes, folderName) {
  return filterNotes(notes, null, folderName).length;
}

function currentUid() {
  return authState.currentUser ? authState.currentUser.uid : null;
}

export async function setNoteFolder(noteID, folderName) {
  const uid = currentUid();
  if (!uid || !noteID) throw new Error("not-authenticated");
  const ref = doc(db, "users", uid, "notes", noteID);
  await updateDoc(ref, {
    folder: folderName && folderName.length > 0 ? folderName : null,
  });
}

export async function trashNote(noteID) {
  const uid = currentUid();
  if (!uid || !noteID) throw new Error("not-authenticated");
  const ref = doc(db, "users", uid, "notes", noteID);
  await updateDoc(ref, { deleted: true, timestamp: serverTimestamp() });
}

export async function restoreNote(noteID) {
  const uid = currentUid();
  if (!uid || !noteID) throw new Error("not-authenticated");
  const ref = doc(db, "users", uid, "notes", noteID);
  await updateDoc(ref, { deleted: false, timestamp: serverTimestamp() });
}

export async function purgeNote(noteID) {
  const uid = currentUid();
  if (!uid || !noteID) throw new Error("not-authenticated");
  await deleteDoc(doc(db, "users", uid, "notes", noteID));
  // Its handwriting, pictures and files go too, once the note is gone for
  // good (NoteStorageCleanup.swift). In the background: the note is
  // already deleted, and a file left behind harms nothing.
  deleteNoteAssets(uid, noteID, { storage, ref: storageRef, listAll, deleteObject });
}

// ---------------------------------------------------------------------------
// Sharing — mirrors MenuItem.swift:240-323
// sharedWith: [{userID, access}], sharedWithUsersID: [userID, ...]
// And mirrors a row in users/{friendID}/sharedNotes/{ownerID}_{noteID}.

export async function shareNoteWithFriend(noteID, friend, access = "WRITE") {
  const uid = currentUid();
  if (!uid || !noteID || !friend || !friend.friendID) throw new Error("invalid-args");
  const noteRef = doc(db, "users", uid, "notes", noteID);
  const snap = await getDoc(noteRef);
  if (!snap.exists()) throw new Error("note-not-found");
  const data = snap.data() || {};
  const current = Array.isArray(data.sharedWith) ? [...data.sharedWith] : [];
  const filtered = current.filter((s) => s && s.userID !== friend.friendID);
  filtered.push({ userID: friend.friendID, access: String(access).toUpperCase() });
  const ids = filtered.map((s) => s.userID).filter(Boolean);
  await updateDoc(noteRef, { sharedWith: filtered, sharedWithUsersID: ids });

  const friendRef = doc(
    db,
    "users",
    friend.friendID,
    "sharedNotes",
    `${uid}_${noteID}`,
  );
  const ownerName = (authState.userData && authState.userData.name) || "";
  await setDoc(friendRef, { noteID, userID: uid, name: ownerName }, { merge: true });
}

export async function unshareNoteWithFriend(noteID, friendID) {
  const uid = currentUid();
  if (!uid || !noteID || !friendID) throw new Error("invalid-args");
  const noteRef = doc(db, "users", uid, "notes", noteID);
  const snap = await getDoc(noteRef);
  if (!snap.exists()) throw new Error("note-not-found");
  const data = snap.data() || {};
  const current = Array.isArray(data.sharedWith) ? [...data.sharedWith] : [];
  const filtered = current.filter((s) => s && s.userID !== friendID);
  const ids = filtered.map((s) => s.userID).filter(Boolean);
  await updateDoc(noteRef, { sharedWith: filtered, sharedWithUsersID: ids });

  try {
    await deleteDoc(doc(db, "users", friendID, "sharedNotes", `${uid}_${noteID}`));
  } catch (err) {
    console.warn("delete sharedNotes entry failed:", err);
  }
}

// Folder CRUD — mirrors FolderView.swift's Firestore writes.
// Default palette mirrors the iOS picker.
export const FOLDER_COLOR_PALETTE = [
  [0.96, 0.26, 0.21, 1], // red
  [1.0, 0.58, 0.0, 1], // orange
  [1.0, 0.84, 0.0, 1], // yellow
  [0.34, 0.78, 0.32, 1], // green
  [0.0, 0.62, 0.93, 1], // blue
  [0.62, 0.34, 0.86, 1], // purple
  [0.55, 0.55, 0.6, 1], // gray
];

function pickRandomFolderColor() {
  const idx = Math.floor(Math.random() * FOLDER_COLOR_PALETTE.length);
  return [...FOLDER_COLOR_PALETTE[idx]];
}

export async function createFolder(name) {
  const uid = currentUid();
  const trimmed = (name || "").trim();
  if (!uid || !trimmed) throw new Error("invalid-args");
  // De-dupe against the current cached folder list — matches the iOS suffix
  // strategy ("Math 1", "Math 2"…) so the new folder doesn't collide.
  const existing = new Set(state.folders.map((f) => f.name));
  let unique = trimmed;
  let suffix = 1;
  while (existing.has(unique)) {
    unique = `${trimmed} ${suffix}`;
    suffix += 1;
  }
  const folderRef = doc(collection(db, "users", uid, "folders"));
  await setDoc(folderRef, {
    name: unique,
    color: pickRandomFolderColor(),
    createdAt: serverTimestamp(),
  });
  return { id: folderRef.id, name: unique };
}

export async function setFolderColor(folderID, color) {
  const uid = currentUid();
  if (!uid || !folderID) throw new Error("invalid-args");
  if (!Array.isArray(color) || color.length < 3) throw new Error("invalid-color");
  const ref = doc(db, "users", uid, "folders", folderID);
  await updateDoc(ref, { color });
}

export async function renameFolder(folderID, oldName, newName) {
  const uid = currentUid();
  const trimmed = (newName || "").trim();
  if (!uid || !folderID || !trimmed) throw new Error("invalid-args");
  if (trimmed === oldName) return;
  const folderRef = doc(db, "users", uid, "folders", folderID);

  // Update *every* note that references the old folder name (not just the
  // current page), then rename the folder doc — mirrors the iOS batch logic
  // in FolderView.swift:166. Firestore caps batches at 500 writes.
  const notesRef = collection(db, "users", uid, "notes");
  const snap = await getDocs(query(notesRef, where("folder", "==", oldName)));
  const docs = snap.docs;
  if (docs.length === 0) {
    await updateDoc(folderRef, { name: trimmed });
    return;
  }
  const MAX_BATCH = 500;
  let index = 0;
  while (index < docs.length) {
    const batch = writeBatch(db);
    const isFirst = index === 0;
    const remaining = docs.length - index;
    const capacity = isFirst ? MAX_BATCH - 1 : MAX_BATCH;
    const take = Math.min(capacity, remaining);
    for (let offset = 0; offset < take; offset++) {
      batch.update(docs[index + offset].ref, { folder: trimmed });
    }
    if (isFirst) batch.update(folderRef, { name: trimmed });
    await batch.commit();
    index += take;
  }
}

export async function deleteFolder(folderID, name) {
  const uid = currentUid();
  if (!uid || !folderID) throw new Error("invalid-args");
  // Detach folder from any notes that reference it, then drop the folder doc.
  if (name) {
    const notesRef = collection(db, "users", uid, "notes");
    const snap = await getDocs(query(notesRef, where("folder", "==", name)));
    const docs = snap.docs;
    const MAX_BATCH = 500;
    let index = 0;
    while (index < docs.length) {
      const batch = writeBatch(db);
      const take = Math.min(MAX_BATCH, docs.length - index);
      for (let offset = 0; offset < take; offset++) {
        batch.update(docs[index + offset].ref, { folder: null });
      }
      await batch.commit();
      index += take;
    }
  }
  await deleteDoc(doc(db, "users", uid, "folders", folderID));
}

export const notesState = readonly(state);
